import React, { useState, useEffect, useCallback, useRef } from 'react';
import AcademicContextSelector from './AcademicContextSelector';
import api from '../api';

const StudentCieMarksForm = ({ readOnly = false }) => {
  // All state declarations
  const [context, setContext] = useState(null);
  const [loading, setLoading] = useState(false);
  const [students, setStudents] = useState([]);
  const [, setAllStudents] = useState([]); 
  const [classFilter, setClassFilter] = useState('');
  const [availableClasses, setAvailableClasses] = useState([]);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [selectedSubject, setSelectedSubject] = useState(null);
  const [showStudentModal, setShowStudentModal] = useState(false);
  const [showSubjectModal, setShowSubjectModal] = useState(false);
  const [studentSubjects, setStudentSubjects] = useState([]);
  const [subjectDetails, setSubjectDetails] = useState(null);
  const [modalLoading, setModalLoading] = useState(false);
  const [error, setError] = useState(null);
  const [downloadingPdf, setDownloadingPdf] = useState(false);

  // Refs for preventing duplicate operations
  const isMounted = useRef(true);
  const fetchingRef = useRef(false);
  const lastContextRef = useRef(null);
  const isDownloadingRef = useRef(false);
  const downloadTimeoutRef = useRef(null);

  // Component lifecycle
  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
      // Clean up timeouts
      if (downloadTimeoutRef.current) {
        clearTimeout(downloadTimeoutRef.current);
      }
    };
  }, []);

  // Fetch organization info from API
  const fetchOrgInfo = useCallback(async () => {
    try {
      const tenantId = localStorage.getItem('tenantId');
      if (!tenantId) return {};
      
      const response = await api.get(`/api/organization/basic-info/${tenantId}`);
      if (response.data.success) {
        return {
          name: response.data.name || '',
          logoUrl: response.data.logoUrl || '',
          address: response.data.address || '',
          phone: response.data.phone || '',
          email: response.data.email || ''
        };
      }
      return {};
    } catch (err) {
      console.error('Error fetching org info:', err);
      return {};
    }
  }, []);

  // Fetch available classes
  useEffect(() => {
    if (!context) return;
    
    const semesterOrTermId = context.evaluationMode === 'sem' ? context.semId : context.termId;
    const semesterOrTermType = context.evaluationMode === 'sem' ? 'semester' : 'term';
    
    if (!context.programTypeId || !context.programId || !context.academicYearId || !semesterOrTermId) {
      setAvailableClasses([]);
      return;
    }

    const fetchClasses = async () => {
      try {
        const params = new URLSearchParams({
          programTypeId: context.programTypeId,
          programId: context.programId,
          academicYearId: context.academicYearId,
          semesterOrTermId,
          semesterOrTermType
        });
        const response = await api.get(`/api/coe/student-cie/classes?${params}`);
        if (response.data.success) {
          setAvailableClasses(response.data.classes || []);
        } else {
          setAvailableClasses([]);
        }
      } catch (err) {
        console.error('Error fetching classes:', err);
        setAvailableClasses([]);
      }
    };
    
    fetchClasses();
  }, [context]);

  // Fetch students
  const fetchStudents = useCallback(async (ctx, semesterOrTermId, semesterOrTermType) => {
    if (fetchingRef.current) return;
    
    fetchingRef.current = true;
    if (isMounted.current) setLoading(true);
    if (isMounted.current) setError(null);
    
    try {
      const params = new URLSearchParams({
        programTypeId: ctx.programTypeId,
        programId: ctx.programId,
        academicYearId: ctx.academicYearId,
        semesterOrTermId: semesterOrTermId,
        semesterOrTermType: semesterOrTermType
      });

      const response = await api.get(`/api/coe/student-cie/view?${params}`);
      
      if (isMounted.current && response.data.success) {
        const responseData = response.data.data;
        let studentsData = responseData.students || [];
        
        const seen = new Set();
        studentsData = studentsData.filter(s => {
          if (seen.has(s.studentId)) return false;
          seen.add(s.studentId);
          return true;
        });
        
        studentsData = [...studentsData].sort((a, b) => {
          const usnA = a.studentUSN || '';
          const usnB = b.studentUSN || '';
          return usnA.localeCompare(usnB);
        });
        
        setStudents(studentsData);
        
        setAllStudents(prev => {
          const all = [...prev, ...studentsData];
          const seenAll = new Set();
          return all.filter(s => {
            if (seenAll.has(s.studentId)) return false;
            seenAll.add(s.studentId);
            return true;
          });
        });
      }
    } catch (err) {
      console.error('Error fetching students:', err);
      if (isMounted.current) {
        setError(err.response?.data?.message || 'Failed to fetch students');
      }
    } finally {
      if (isMounted.current) setLoading(false);
      fetchingRef.current = false;
    }
  }, []);

  // Sync students data
  useEffect(() => {
    if (!context) return;
    
    const semesterOrTermId = context.evaluationMode === 'sem' ? context.semId : context.termId;
    const semesterOrTermType = context.evaluationMode === 'sem' ? 'semester' : 'term';
    
    if (!context.programTypeId || !context.programId || !context.academicYearId || !semesterOrTermId) {
      return;
    }

    const contextKey = `${context.programTypeId}_${context.programId}_${context.academicYearId}_${semesterOrTermId}_${semesterOrTermType}`;
    if (lastContextRef.current === contextKey) return;
    lastContextRef.current = contextKey;

    fetchStudents(context, semesterOrTermId, semesterOrTermType);
  }, [context, fetchStudents]);

  // Fetch student subjects
  const fetchStudentSubjects = useCallback(async (studentId) => {
    if (!context) return null;
    
    const semesterOrTermId = context.evaluationMode === 'sem' ? context.semId : context.termId;
    const semesterOrTermType = context.evaluationMode === 'sem' ? 'semester' : 'term';
    
    try {
      const params = new URLSearchParams({
        studentId: studentId,
        academicYearId: context.academicYearId,
        semesterOrTermId: semesterOrTermId,
        semesterOrTermType: semesterOrTermType,
        programTypeId: context.programTypeId,
        programId: context.programId
      });

      const response = await api.get(`/api/coe/student-cie/student-subjects?${params}`);
      if (response.data.success) {
        return response.data.data;
      }
      return null;
    } catch (err) {
      console.error('Error fetching student subjects:', err);
      return null;
    }
  }, [context]);

  // Fetch subject component details
  const fetchSubjectComponentDetails = useCallback(async (studentId, subjectId) => {
    if (!context) return null;
    
    const semesterOrTermId = context.evaluationMode === 'sem' ? context.semId : context.termId;
    const semesterOrTermType = context.evaluationMode === 'sem' ? 'semester' : 'term';
    
    try {
      const params = new URLSearchParams({
        studentId: studentId,
        subjectId: subjectId,
        academicYearId: context.academicYearId,
        semesterOrTermId: semesterOrTermId,
        semesterOrTermType: semesterOrTermType,
        programTypeId: context.programTypeId,
        programId: context.programId
      });

      const response = await api.get(`/api/coe/student-cie/component-wise?${params}`);
      if (response.data.success) {
        return response.data.data;
      }
      return null;
    } catch (err) {
      console.error('Error fetching subject details:', err);
      return null;
    }
  }, [context]);

  // Handle student click
  const handleStudentClick = async (student) => {
    setSelectedStudent(student);
    setModalLoading(true);
    setShowStudentModal(true);
    
    const subjectsData = await fetchStudentSubjects(student.studentId);
    if (subjectsData) {
      setStudentSubjects(subjectsData.subjects || []);
    }
    setModalLoading(false);
  };

  // Handle subject click
  const handleSubjectClick = async (subject) => {
    setSelectedSubject(subject);
    setModalLoading(true);
    setShowSubjectModal(true);
    
    const details = await fetchSubjectComponentDetails(selectedStudent.studentId, subject.subjectId);
    if (details) {
      setSubjectDetails(details);
    }
    setModalLoading(false);
  };

  // Close modals
  const closeStudentModal = () => {
    setShowStudentModal(false);
    setSelectedStudent(null);
    setStudentSubjects([]);
  };

  const closeSubjectModal = () => {
    setShowSubjectModal(false);
    setSelectedSubject(null);
    setSubjectDetails(null);
  };

  // Download PDF directly on button click
  const handleDownloadPdf = useCallback(async (student) => {
    if (isDownloadingRef.current || downloadingPdf) {
      console.log('[PDF] Download already in progress, skipping...');
      alert('PDF generation is already in progress. Please wait...');
      return;
    }
    
    if (!context || !student) {
      alert('Please select a student first');
      return;
    }
    
    console.log('[PDF] Starting PDF generation for student:', student.studentUSN);
    
    isDownloadingRef.current = true;
    setDownloadingPdf(true);
    
    const semesterOrTermId = context.evaluationMode === 'sem' ? context.semId : context.termId;
    const semesterOrTermType = context.evaluationMode === 'sem' ? 'semester' : 'term';

    try {
      const orgInfo = await fetchOrgInfo();
      console.log('[PDF] Organization info fetched:', orgInfo);

      const params = new URLSearchParams({
        tenantId: localStorage.getItem('tenantId'),
        programTypeId: context.programTypeId,
        programId: context.programId,
        academicYearId: context.academicYearId,
        semesterOrTermId,
        semesterOrTermType,
        studentId: student.studentId
      });

      const apiUrl = `/api/coe/student-cie/pdf?${params.toString()}`;
      console.log('[PDF] Request URL:', apiUrl);
      
      const response = await api.post(apiUrl, {
        orgInfo,
        signatories: []
      }, {
        responseType: 'arraybuffer',
        timeout: 60000,
        headers: {
          'Accept': 'application/pdf',
          'Content-Type': 'application/json'
        }
      });
      
      const contentType = response.headers['content-type'] || response.headers['Content-Type'];
      console.log('[PDF] Response content type:', contentType);
      
      if (!response.data || response.data.byteLength === 0) {
        throw new Error('Empty response received');
      }
      
      const buffer = new Uint8Array(response.data);
      const isPdf = buffer.length > 4 && 
                    buffer[0] === 0x25 && buffer[1] === 0x50 && 
                    buffer[2] === 0x44 && buffer[3] === 0x46;
      
      if (!isPdf) {
        const decoder = new TextDecoder('utf-8');
        const errorText = decoder.decode(buffer.slice(0, 500));
        console.error('[PDF] Invalid PDF response:', errorText);
        
        try {
          const errorJson = JSON.parse(errorText);
          throw new Error(errorJson.message || 'Invalid PDF format received');
        } catch (e) {
          throw new Error('Invalid PDF format received. Please try again.');
        }
      }
      
      console.log('[PDF] Valid PDF detected, size:', response.data.byteLength, 'bytes');
      
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const blobUrl = window.URL.createObjectURL(blob);
      
      console.log('[PDF] Blob created, size:', blob.size, 'bytes');
      
      const downloadLink = document.createElement('a');
      downloadLink.href = blobUrl;
      downloadLink.download = `${student.studentUSN || 'student'}_CIE_Report.pdf`;
      downloadLink.style.display = 'none';
      document.body.appendChild(downloadLink);
      
      downloadLink.click();
      
      setTimeout(() => {
        document.body.removeChild(downloadLink);
        window.URL.revokeObjectURL(blobUrl);
        console.log('[PDF] Cleanup completed');
      }, 100);
      
      console.log('[PDF] Download initiated successfully');
      
    } catch (err) {
      console.error('[PDF] Download error:', err);
      
      let errorMessage = 'Failed to download PDF. Please try again.';
      if (err.message) {
        errorMessage = err.message;
      }
      
      alert(errorMessage);
    } finally {
      downloadTimeoutRef.current = setTimeout(() => {
        isDownloadingRef.current = false;
        setDownloadingPdf(false);
      }, 1000);
    }
  }, [context, fetchOrgInfo, downloadingPdf]);

  const filteredStudents = classFilter 
    ? students.filter(student => student.className === classFilter)
    : students;

  if (loading && students.length === 0) {
    return (
      <div className="flex flex-col justify-center items-center h-80 space-y-4">
        <div className="relative w-16 h-16">
          <div className="absolute inset-0 rounded-full border-4 border-slate-100"></div>
          <div className="absolute inset-0 rounded-full border-4 border-indigo-600 border-t-transparent animate-spin"></div>
        </div>
        <p className="text-sm font-medium text-slate-500 tracking-wide animate-pulse">Synchronizing student records...</p>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 space-y-6 bg-slate-50/50 min-h-screen">
      <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-4 sm:p-6 transition-all">
        <AcademicContextSelector
          tenantId={localStorage.getItem('tenantId')}
          onContextChange={setContext}
          initialContext={context}
        />
      </div>

      {error && (
        <div className="bg-rose-50 border border-rose-100 text-rose-800 px-4 py-3.5 rounded-xl flex items-center justify-between shadow-sm animate-fade-in">
          <div className="flex items-center space-x-3">
            <svg className="w-5 h-5 text-rose-500 shrink-0" fill="currentColor" viewBox="0 0 24 24">
              <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z"/>
            </svg>
            <span className="text-sm font-medium">{error}</span>
          </div>
          <button onClick={() => setError(null)} className="text-rose-500 hover:text-rose-700 p-1 rounded-lg hover:bg-rose-100/50 transition-colors">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
          </button>
        </div>
      )}

      {/* Student Directory Panel */}
      {context && filteredStudents.length > 0 && (
        <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden transition-all">
          <div className="px-6 py-5 bg-slate-900 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h2 className="text-xl font-bold text-white tracking-tight">Student Directory</h2>
              <p className="text-slate-400 text-xs sm:text-sm mt-0.5">Select a student row or card to audit continuous internal evaluation parameters.</p>
            </div>
            <div className="shrink-0 flex items-center bg-slate-800 rounded-xl p-1.5 border border-slate-700">
              <label htmlFor="classFilter" className="text-xs font-semibold text-slate-300 px-2.5 uppercase tracking-wider">Class Filter</label>
              <select
                id="classFilter"
                value={classFilter}
                onChange={e => setClassFilter(e.target.value)}
                className="rounded-lg px-3 py-1.5 bg-slate-900 border-none text-xs font-medium text-white outline-none focus:ring-2 focus:ring-indigo-500 transition-all cursor-pointer"
              >
                <option value="">All ({students.length})</option>
                {availableClasses.map(cls => (
                  <option key={cls} value={cls}>{cls} ({students.filter(s => s.className === cls).length})</option>
                ))}
              </select>
            </div>
          </div>

          {/* Responsive Layout: Mobile Cards First */}
          <div className="block sm:hidden divide-y divide-slate-100 max-h-[65vh] overflow-y-auto">
            {filteredStudents.map((student, index) => (
              <div 
                key={student.studentId}
                onClick={() => handleStudentClick(student)}
                className="p-4 hover:bg-slate-50/80 active:bg-slate-100 transition-colors cursor-pointer space-y-3"
              >
                <div className="flex items-start justify-between">
                  <div>
                    <h4 className="font-semibold text-slate-900 text-sm">{student.studentName}</h4>
                    <p className="text-xs text-slate-500 mt-0.5">{student.studentUSN || 'No USN Attached'}</p>
                  </div>
                  <span className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-indigo-50 text-indigo-700 border border-indigo-100">
                    {student.className}
                  </span>
                </div>
                <div className="flex items-center text-xs text-slate-400 gap-1.5">
                  <svg className="w-3.5 h-3.5 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 002-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" /></svg>
                  <span className="truncate">{student.studentEmail || 'N/A'}</span>
                </div>
              </div>
            ))}
          </div>

          {/* Desktop Responsive Table View */}
          <div className="hidden sm:block overflow-x-auto max-h-[60vh] overflow-y-auto">
            <table className="min-w-full divide-y divide-slate-100 text-left">
              <thead className="bg-slate-50/70 sticky top-0 backdrop-blur-md z-10">
                <tr>
                  <th className="px-6 py-3.5 text-xs font-bold text-slate-500 uppercase tracking-wider w-12">#</th>
                  <th className="px-6 py-3.5 text-xs font-bold text-slate-500 uppercase tracking-wider">Student Profile</th>
                  <th className="px-6 py-3.5 text-xs font-bold text-slate-500 uppercase tracking-wider">USN Identifier</th>
                  <th className="px-6 py-3.5 text-xs font-bold text-slate-500 uppercase tracking-wider">Assigned Section</th>
                  <th className="px-6 py-3.5 text-xs font-bold text-slate-500 uppercase tracking-wider">Communication Channel</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-slate-100">
                {filteredStudents.map((student, index) => (
                  <tr 
                    key={student.studentId} 
                    onClick={() => handleStudentClick(student)}
                    className="hover:bg-slate-50/70 cursor-pointer transition-colors group"
                  >
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-400 font-medium">{index + 1}</td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-semibold text-slate-900 group-hover:text-indigo-600 transition-colors">{student.studentName}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-600 font-mono tracking-tight">{student.studentUSN}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-slate-100 text-slate-800">
                        {student.className}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-500">{student.studentEmail}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {context && students.length === 0 && !loading && (
        <div className="text-center py-16 bg-white rounded-2xl border border-dashed border-slate-200 shadow-sm max-w-xl mx-auto p-6">
          <div className="w-12 h-12 rounded-2xl bg-slate-50 flex items-center justify-center mx-auto text-slate-400 mb-4">
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
            </svg>
          </div>
          <h3 className="text-base font-bold text-slate-900 tracking-tight">No active student allocations</h3>
          <p className="mt-1 text-sm text-slate-500 max-w-xs mx-auto">Verify your active academic structure parameters to retrieve corresponding student scores.</p>
        </div>
      )}

      {/* MODAL 1: Student's Subject-wise CIE Marks */}
      {showStudentModal && selectedStudent && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 overflow-x-hidden overflow-y-auto">
          <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-xs transition-opacity" onClick={closeStudentModal}></div>

          <div className="relative bg-white rounded-2xl text-left overflow-hidden shadow-xl transform transition-all w-full max-w-3xl flex flex-col max-h-[90vh]">
            {/* Header */}
            <div className="bg-slate-900 px-6 py-5 border-b border-slate-800 shrink-0">
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <div>
                  <span className="text-[10px] font-bold text-indigo-400 bg-indigo-950 px-2.5 py-1 rounded-md tracking-wider uppercase">CIE Performance Ledger</span>
                  <h3 className="text-lg font-bold text-white tracking-tight mt-1">{selectedStudent.studentName}</h3>
                  <p className="text-slate-400 text-xs mt-0.5 font-medium">
                    {selectedStudent.studentUSN} &bull; Class {selectedStudent.className}
                  </p>
                </div>
                <div className="flex items-center gap-2 self-end sm:self-center">
                  <button
                    onClick={() => handleDownloadPdf(selectedStudent)}
                    disabled={downloadingPdf}
                    className="h-9 px-3.5 bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 text-white rounded-xl text-xs font-semibold flex items-center gap-2 transition-all shadow-sm active:scale-98"
                  >
                    {downloadingPdf ? (
                      <>
                        <div className="w-3.5 h-3.5 rounded-full border-2 border-white/30 border-t-white animate-spin"></div>
                        <span>Processing Report...</span>
                      </>
                    ) : (
                      <>
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
                        <span>Export PDF</span>
                      </>
                    )}
                  </button>
                  <button onClick={closeStudentModal} className="h-9 w-9 bg-slate-800 text-slate-400 hover:text-white rounded-xl flex items-center justify-center border border-slate-700 transition-colors">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                  </button>
                </div>
              </div>
            </div>

            {/* Scrollable Box */}
            <div className="p-6 overflow-y-auto space-y-4 bg-slate-50/50 flex-1">
              <div className="flex items-center space-x-2 text-xs font-semibold text-slate-400 tracking-wide uppercase mb-1">
                <svg className="w-4 h-4 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                <span>Course Enrollments Matrix</span>
              </div>
              
              {modalLoading ? (
                <div className="flex flex-col justify-center items-center h-48 space-y-2">
                  <div className="w-10 h-10 rounded-full border-3 border-slate-100 border-t-indigo-600 animate-spin"></div>
                  <p className="text-xs font-medium text-slate-400">Compiling courses data...</p>
                </div>
              ) : (
                <div className="grid gap-3 sm:grid-cols-1">
                  {studentSubjects.map((subject, idx) => (
                    <div 
                      key={subject.subjectId || idx}
                      onClick={() => handleSubjectClick(subject)}
                      className="bg-white border border-slate-100 rounded-xl p-4 hover:border-indigo-200 hover:shadow-md transition-all cursor-pointer flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 group"
                    >
                      <div className="space-y-1.5">
                        <div className="font-semibold text-slate-900 text-sm group-hover:text-indigo-600 transition-colors">{subject.subjectName}</div>
                        <div className="flex flex-wrap gap-1.5 items-center">
                          <span className="text-[11px] font-mono font-bold text-slate-500 bg-slate-100 px-2 py-0.5 rounded-md">{subject.subjectCode}</span>
                          <span className="text-slate-300 text-xs">&bull;</span>
                          <span className="text-xs text-slate-500 font-medium">{subject.subjectType}</span>
                        </div>
                        <div className="flex flex-wrap gap-1 mt-1">
                          {subject.isLab && <span className="text-[10px] font-bold uppercase tracking-wider bg-purple-50 text-purple-700 border border-purple-100 px-2 py-0.5 rounded-md">Laboratory</span>}
                          {subject.isLanguage && <span className="text-[10px] font-bold uppercase tracking-wider bg-emerald-50 text-emerald-700 border border-emerald-100 px-2 py-0.5 rounded-md">Language</span>}
                          {subject.isElective && <span className="text-[10px] font-bold uppercase tracking-wider bg-amber-50 text-amber-700 border border-amber-100 px-2 py-0.5 rounded-md">Elective</span>}
                        </div>
                      </div>
                      
                      <div className="shrink-0 flex items-center sm:text-right justify-between sm:justify-end border-t border-slate-50 sm:border-none pt-3 sm:pt-0">
                        <span className="sm:hidden text-xs font-semibold text-slate-400 uppercase tracking-wider">Evaluation Total</span>
                        {subject.status === 'entered' && subject.cieMarks ? (
                          <div className="flex flex-col sm:items-end">
                            <div className="flex items-baseline space-x-0.5">
                              <span className="text-xl font-bold text-slate-900 tracking-tight">{subject.cieMarks.totalCIEScore || 0}</span>
                              <span className="text-xs text-slate-400 font-medium">/{subject.cieMarks.cieMaxMarks || 40}</span>
                            </div>
                            <span className="text-[11px] font-semibold text-slate-500 mt-0.5 bg-slate-50 px-1.5 py-0.5 rounded">
                              {subject.cieMarks.ciePercentage?.toFixed(1) || 0}% Achievement
                            </span>
                            {subject.cieMarks.isEligible ? (
                              <span className="inline-flex items-center px-2 py-0.5 rounded-md text-[10px] font-bold uppercase tracking-wide bg-emerald-50 text-emerald-700 border border-emerald-100 mt-1.5">Eligible</span>
                            ) : (
                              <span className="inline-flex items-center px-2 py-0.5 rounded-md text-[10px] font-bold uppercase tracking-wide bg-rose-50 text-rose-700 border border-rose-100 mt-1.5">Not Eligible</span>
                            )}
                          </div>
                        ) : (
                          <span className="inline-flex items-center px-2.5 py-1 rounded-md text-xs font-semibold bg-amber-50 text-amber-800 border border-amber-100">Pending Evaluation</span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="bg-slate-50 px-6 py-4 border-t border-slate-100 flex justify-end shrink-0">
              <button onClick={closeStudentModal} className="h-9 px-4 bg-white hover:bg-slate-50 text-slate-700 border border-slate-200 text-xs font-semibold rounded-xl shadow-xs transition-colors">
                Dismiss Pane
              </button>
            </div>
          </div>
        </div>
      )}

      {/* MODAL 2: Detailed Component-wise Marks for a Subject */}
      {showSubjectModal && selectedSubject && subjectDetails && (
        <div className="fixed inset-0 z-60 flex items-center justify-center p-4 overflow-x-hidden overflow-y-auto">
          <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-xs transition-opacity" onClick={closeSubjectModal}></div>

          <div className="relative bg-white rounded-2xl text-left overflow-hidden shadow-2xl transform transition-all w-full max-w-2xl flex flex-col max-h-[85vh]">
            {/* Header */}
            <div className="bg-gradient-to-r from-indigo-900 to-slate-900 px-6 py-5 border-b border-indigo-950 shrink-0">
              <div className="flex items-start justify-between">
                <div>
                  <span className="text-[10px] font-bold text-indigo-300 bg-indigo-950/60 px-2.5 py-1 rounded-md tracking-wider uppercase">Component Parameter Breakdown</span>
                  <h3 className="text-lg font-bold text-white tracking-tight mt-1.5">{selectedSubject.subjectName}</h3>
                  <p className="text-slate-400 text-xs mt-0.5 font-medium font-mono">
                    {subjectDetails.studentName} &bull; {subjectDetails.subjectCode}
                  </p>
                </div>
                <button onClick={closeSubjectModal} className="h-8 w-8 bg-indigo-950/40 text-indigo-300 hover:text-white rounded-lg flex items-center justify-center border border-indigo-800/50 transition-colors">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                </button>
              </div>
            </div>

            {/* Scrollable Payload Container */}
            <div className="p-6 overflow-y-auto space-y-5 bg-slate-50/50 flex-1">
              {/* Summary Dashboard Badge */}
              <div className="bg-white border border-slate-100 rounded-2xl p-4 shadow-xs grid grid-cols-2 gap-4 divide-x divide-slate-100">
                <div className="space-y-1">
                  <span className="text-[11px] font-bold text-slate-400 uppercase tracking-wider">CIE Aggregation</span>
                  <div className="flex items-baseline space-x-0.5">
                    <p className="text-2xl font-black text-indigo-600 tracking-tight">{subjectDetails.totalCIEScore}</p>
                    <p className="text-sm font-bold text-slate-400">/{subjectDetails.cieMaxMarks}</p>
                  </div>
                  <p className="text-xs font-medium text-slate-500">({subjectDetails.ciePercentage?.toFixed(1)}% Score Value)</p>
                </div>
                <div className="pl-4 space-y-1.5">
                  <span className="text-[11px] font-bold text-slate-400 uppercase tracking-wider">Board Clearance</span>
                  <div className="flex items-center">
                    {subjectDetails.isEligible ? (
                      <span className="inline-flex items-center px-2.5 py-1 rounded-md text-xs font-bold uppercase tracking-wide bg-emerald-50 text-emerald-700 border border-emerald-100">Cleared/Eligible</span>
                    ) : (
                      <span className="inline-flex items-center px-2.5 py-1 rounded-md text-xs font-bold uppercase tracking-wide bg-rose-50 text-rose-700 border border-rose-100">Disqualified</span>
                    )}
                  </div>
                  {subjectDetails.eligibilityReason && (
                    <p className="text-xs font-medium text-slate-400 leading-tight">{subjectDetails.eligibilityReason}</p>
                  )}
                </div>
              </div>

              {/* Component Trees */}
              {subjectDetails.components && subjectDetails.components.length > 0 ? (
                <div className="space-y-4">
                  <h4 className="text-xs font-bold text-slate-400 uppercase tracking-wider">Evaluation Component Tree</h4>
                  
                  {subjectDetails.components.map((component, idx) => (
                    <div key={component.componentId || idx} className="bg-white border border-slate-100 rounded-xl overflow-hidden shadow-xs">
                      {/* Component Parent Header Layout */}
                      <div className="bg-slate-50/80 px-4 py-3.5 border-b border-slate-100 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                        <div>
                          <span className="font-semibold text-slate-900 text-sm">{component.componentName}</span>
                          <div className="flex flex-wrap gap-1.5 items-center mt-0.5">
                            <span className="text-[10px] font-bold text-indigo-600 bg-indigo-50 px-1.5 py-0.5 rounded">Weightage: {component.weightageInCIE}%</span>
                            {component.aggregationRule && component.aggregationRule !== 'single_component' && (
                              <span className="text-[10px] font-medium bg-slate-200 text-slate-700 px-1.5 py-0.5 rounded-sm">{component.aggregationRule}</span>
                            )}
                          </div>
                        </div>
                        <div className="sm:text-right flex sm:flex-col items-baseline justify-between sm:justify-end border-t border-slate-100/60 sm:border-none pt-2 sm:pt-0">
                          <div className="flex items-baseline space-x-0.5">
                            <span className="font-bold text-slate-900 text-base">{component.rawScore}</span>
                            <span className="text-xs text-slate-400 font-medium">/{component.maxRawScore}</span>
                          </div>
                          <div className="text-[10px] font-semibold text-slate-400">Yield: {component.contributionToCIE?.toFixed(2)} Points</div>
                        </div>
                      </div>

                      {/* Child Mapping Assessments */}
                      {component.childItems && component.childItems.length > 0 ? (
                        <div className="px-4 py-3 bg-white divide-y divide-slate-50">
                          {component.childItems.map((item, itemIdx) => (
                            <div key={item.itemId || itemIdx} className="flex items-center justify-between py-2.5 first:pt-1 last:pb-1 group">
                              <div className="flex items-center space-x-2.5">
                                <div className="w-1.5 h-1.5 rounded-full bg-slate-300 group-hover:bg-indigo-500 transition-colors"></div>
                                <span className="text-xs font-medium text-slate-700">{item.itemName}</span>
                              </div>
                              <div className="text-right flex items-center space-x-3">
                                <div className="flex items-baseline">
                                  <span className="text-xs font-bold text-slate-900">{item.rawScore}</span>
                                  <span className="text-[10px] text-slate-400 font-medium">/{item.maxRawScore}</span>
                                </div>
                                <span className="inline-block text-[10px] font-mono font-bold px-1.5 py-0.5 bg-slate-50 rounded text-slate-500 min-w-[45px]">
                                  {item.percentage?.toFixed(1)}%
                                </span>
                              </div>
                            </div>
							            ))}
                        </div>
                      ) : (
                        <div className="px-4 py-3 bg-white text-xs text-slate-400 italic">
                          Leaf evaluation node. No segmented internal assessments attached.
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-slate-400 text-xs font-medium">
                  No structural parameter segments mapped for this domain.
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="bg-slate-50 px-6 py-4 border-t border-slate-100 flex justify-end shrink-0">
              <button onClick={closeSubjectModal} className="h-9 px-4 bg-white hover:bg-slate-50 text-slate-700 border border-slate-200 text-xs font-semibold rounded-xl shadow-xs transition-colors">
                Return to Ledger
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default StudentCieMarksForm;